import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { ShieldAlert, Loader2, Wrench } from "lucide-react";
import { useSessao } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { DOMINIOS_PERMITIDOS } from "@/lib/firebase";

export const Route = createFileRoute("/auth")({ component: Entrar });

/** A marca: pílula do acento com a inicial em serifa. */
function Marca() {
  return (
    <Link to="/" className="inline-flex items-center gap-2.5">
      <span className="font-display flex size-8 items-center justify-center rounded-full bg-emerald text-base text-background">
        P
      </span>
      <span className="font-display text-xl">Parker</span>
      <span className="label-caps">Lonepine</span>
    </Link>
  );
}

function Entrar() {
  const { estado, erro, erroDeConfiguracao, entrar, sair } = useSessao();

  if (estado === "liberado") return <Navigate to="/painel" />;

  const ocupado = estado === "carregando" || estado === "liberando";

  return (
    <main className="flex min-h-dvh flex-col justify-center bg-background px-6 py-12">
      <div className="mx-auto w-full max-w-md">
        <Marca />
        <hr className="rule-horizon mt-8" />

        {estado === "negado" ? (
          <>
            <p className="label-caps mt-10 flex items-center gap-1.5 text-negative">
              <ShieldAlert className="size-3.5" /> Acesso não liberado
            </p>
            <h1 className="font-display mt-3 text-3xl">Sua conta ainda não entra aqui</h1>
            <p className="mt-4 text-sm text-muted-foreground">
              {erro ?? "Esta conta ainda não tem papel atribuído no Parker."}
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Peça a um administrador do grupo para liberar seu acesso em Cadastros · Usuários.
            </p>
            <Button variant="outline" className="mt-8 w-full" onClick={() => void sair()}>
              Entrar com outra conta
            </Button>
          </>
        ) : (
          <>
            <h1 className="font-display mt-10 text-4xl">Entrar</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Controle gerencial do grupo Lonepine. Use sua conta Google corporativa
              {DOMINIOS_PERMITIDOS.length === 1 ? ` @${DOMINIOS_PERMITIDOS[0]}` : ""}.
            </p>

            <Button
              className="mt-8 w-full"
              size="lg"
              onClick={() => void entrar()}
              disabled={ocupado}
            >
              {ocupado ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Entrando…
                </>
              ) : (
                "Continuar com Google"
              )}
            </Button>

            {/*
              Falha de configuração não é "tente de novo": tentar de novo não
              resolve. O bloco troca o tom, nomeia o que falta e diz de quem é a
              próxima ação. Quando o provedor for habilitado, este código deixa
              de acontecer — nada fica mascarado.
            */}
            {erro && erroDeConfiguracao ? (
              <div className="mt-8 border-t border-hairline pt-6">
                <p className="label-caps flex items-center gap-1.5 text-warning">
                  <Wrench className="size-3.5" /> Falta um passo de configuração
                </p>
                <p className="mt-3 text-sm text-foreground">{erro}</p>
                <p className="mt-3 text-sm text-muted-foreground">
                  Enquanto isso não for feito, ninguém consegue entrar — nem com a conta certa. Não
                  é um problema da sua conta nem do seu navegador.
                </p>
              </div>
            ) : erro ? (
              <p className="mt-6 text-sm text-negative" role="alert">
                {erro}
              </p>
            ) : null}
          </>
        )}
      </div>
    </main>
  );
}
